// BusinessNameTypeahead - Search existing businesses or create new on save
import { useState, useEffect, useRef } from "react";
import { Building2, Plus, Check, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BusinessNameTypeahead - Typeahead for business name with auto-create capability
 * 
 * @param {string} value - Current business name value
 * @param {string} customerId - Currently linked customer ID (if any)
 * @param {function} onSelectExisting - Called when selecting an existing customer
 * @param {function} onCreateNew - Called when user chooses to create a new business
 * @param {function} onChange - Called on input change
 * @param {function} onClear - Called when clearing selection
 */
export default function BusinessNameTypeahead({
  value = "",
  customerId,
  onSelectExisting,
  onCreateNew,
  onChange,
  onClear,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Sync input value with prop
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!inputValue || inputValue.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await axios.get(`${API}/api/customers/search`, {
          params: { q: inputValue, limit: 6 }
        });
        setSearchResults(res.data.results || []);
      } catch (err) {
        console.error("Search failed:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    if (newValue.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleSelectExisting = (customer) => {
    setInputValue(customer.business_name);
    setIsOpen(false);
    onSelectExisting?.(customer);
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    onCreateNew?.(inputValue);
  };

  const handleClear = () => {
    setInputValue("");
    setSearchResults([]);
    onClear?.();
  };

  const handleFocus = () => {
    if (inputValue.length >= 2) {
      setIsOpen(true);
    }
  };

  // Check if input matches any existing business (case-insensitive)
  const hasExactMatch = searchResults.some(
    (c) => c.business_name?.toLowerCase() === inputValue?.toLowerCase()
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search or enter new business name..."
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          data-testid="client-business-name-input"
          className="pr-16"
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isSearching && (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          )}
          {inputValue && !isSearching && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={handleClear}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && inputValue.length >= 2 && (
        <div className="absolute z-50 top-full mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200 max-h-64 overflow-hidden">
          {/* Existing businesses */}
          {searchResults.length > 0 && (
            <div className="py-1 border-b">
              <div className="px-3 py-1 text-xs text-muted-foreground font-medium">
                Existing Businesses
              </div>
              {searchResults.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => handleSelectExisting(customer)}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left transition-colors"
                >
                  <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{customer.business_name}</div>
                    {customer.contact_name && (
                      <div className="text-xs text-muted-foreground truncate">{customer.contact_name}</div>
                    )}
                  </div>
                  {customerId === customer.id && (
                    <Check className="w-4 h-4 text-green-600 shrink-0" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Create new option */}
          {!hasExactMatch && inputValue.length >= 2 && (
            <div className="py-1">
              <button
                type="button"
                onClick={handleCreateNew}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-blue-50 text-left transition-colors text-primary"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span className="text-sm">
                  Create: <strong>{inputValue}</strong>
                </span>
              </button>
            </div>
          )}

          {/* No results and no create option */}
          {searchResults.length === 0 && hasExactMatch && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No businesses found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
